<template>
  <div id="app">
    <div class="loader-container" :class="{ 'fade-out': !isLoading }">
      <div class="loader">
        <svg class="loader-svg" viewBox="0 0 50 50">
          <circle class="loader-circle" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
        <div class="loader-text">VA Video to MP3 converter</div>
      </div>
    </div>

    <div class="content-container" :class="{ 'fade-in': !isLoading }">
      <div class="gradient-bg"></div>
      <div class="wave-bg"></div>
      <div class="particles">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
      </div>
      <div class="decorative-circle circle-1"></div>
      <div class="decorative-circle circle-2"></div>
      <div class="decorative-line line-1"></div>
      <div class="decorative-line line-2"></div>
      <div class="content-wrapper">
        <header class="header">
          <div class="logo-container">
            <img alt="VA Video to MP3 converter Logo" class="logo" src="./assets/logo.png">
            <div class="app-title-container">
              <h1>VA Video to MP3 converter</h1>
              <h2 class="app-subtitle">Video to MP3 Cutter</h2>
            </div>
          </div>
        </header>
        
        <main class="main-content">
          <div class="hero">
            <h2>Convert Videos to MP3 in Seconds</h2>
            <p class="subtitle">Easy-to-use MP3 converter and audio cutter with professional quality</p>
            <div class="hero-cta">
              <a href="#download" class="cta-btn pulse-animation">
                <span class="btn-text">Download Now</span>
                <span class="btn-icon">↓</span>
              </a>
            </div>
            <div class="hero-backdrop"></div>
          </div>
          
          <div id="download" class="download-section">
            <div class="download-badge">
              <span>Official Download</span>
            </div>
            <h3>Download for Your Device</h3>
            <p class="download-desc">Free video to MP3 converter with professional quality output</p>
            
            <div class="download-options">
              <div class="download-option android" :class="{ 'order-first': isAndroid }">
                <div class="platform-label">Android</div>
                <div class="icon-container">
                  <img src="./assets/android-icon.svg" alt="Android" class="platform-icon">
                </div>
                <h4>Android Version</h4>
                <div class="version-wrapper">
                  <div class="version">Latest: v3.2.1</div>
                  <div class="rating">
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star-half">★</span>
                    <span class="rating-number">4.8</span>
                  </div>
                </div>
                <div class="compatible">Compatible with Android 6.0 and above</div>
                <a href="#" class="download-btn android-btn" @click.prevent="showAndroidNotice">
                  <span class="btn-text">Download for Android</span>
                  <span class="btn-icon">→</span>
                </a>
                <div class="download-meta">
                  <div class="meta-item">
                    <span class="meta-icon">📦</span>
                    <span class="download-size">24.5 MB</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-icon">🔄</span>
                    <span class="download-date">Updated: Aug 2023</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-icon">👥</span>
                    <span class="download-count">5M+ Downloads</span>
                  </div>
                </div>
                <div class="qr-download">
                  <div class="qr-code">
                    <div class="qr-inner"></div>
                  </div>
                  <p>Scan QR to download</p>
                </div>
              </div>
              
              <div class="download-option ios" :class="{ 'order-first': isIOS }">
                <div class="platform-label">iOS</div>
                <div class="icon-container">
                  <img src="./assets/ios-icon.svg" alt="iOS" class="platform-icon">
                </div>
                <h4>iOS Version</h4>
                <div class="version-wrapper">
                  <div class="version">Latest: v3.2.0</div>
                  <div class="rating">
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="star">★</span>
                    <span class="rating-number">4.9</span>
                  </div>
                </div>
                <div class="compatible">Compatible with iOS 12.0 and above</div>
                <a href="https://apps.apple.com/app/id6739273941" class="download-btn ios-btn">
                  <span class="btn-text">App Store Download</span>
                  <span class="btn-icon">→</span>
                </a>
                <div class="download-meta">
                  <div class="meta-item">
                    <span class="meta-icon">📦</span>
                    <span class="download-size">32.1 MB</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-icon">🔄</span>
                    <span class="download-date">Updated: Aug 2023</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-icon">👥</span>
                    <span class="download-count">3M+ Downloads</span>
                  </div>
                </div>
                <div class="qr-download">
                  <div class="qr-code">
                    <div class="qr-inner"></div>
                  </div>
                  <p>Scan QR to download</p>
                </div>
              </div>
            </div>
            
            <div class="download-info">
              <div class="info-item">
                <div class="info-icon">🔒</div>
                <div class="info-text">
                  <h5>Safe & Secure</h5>
                  <p>All downloads are verified and scanned for viruses</p>
                </div>
              </div>
              <div class="info-item">
                <div class="info-icon">💯</div>
                <div class="info-text">
                  <h5>No Ads</h5>
                  <p>Clean interface with no intrusive advertisements</p>
                </div>
              </div>
              <div class="info-item">
                <div class="info-icon">🆓</div>
                <div class="info-text">
                  <h5>Free to Use</h5>
                  <p>All core features available without payment</p>
                </div>
              </div>
            </div>
            
            <div class="download-notice">
              <div class="notice-icon">ⓘ</div>
              <p>All downloads are verified and secure. For alternative download options, please <a href="#" class="notice-link">contact our support team</a>.</p>
            </div>
          </div>
          
          <div class="features">
            <h3>Powerful Features</h3>
            <div class="feature-list">
              <div class="feature-item">
                <div class="feature-icon">🎬</div>
                <h4>Video to MP3 Conversion</h4>
                <p>Convert any video format to high-quality MP3 audio with just a few taps</p>
              </div>
              
              <div class="feature-item">
                <div class="feature-icon">✂️</div>
                <h4>Audio Cutter</h4>
                <p>Precisely cut and trim your audio files to extract only the parts you want</p>
              </div>
              
              <div class="feature-item">
                <div class="feature-icon">🔊</div>
                <h4>Quality Control</h4>
                <p>Adjust bitrate and audio quality settings to balance between quality and file size</p>
              </div>
            </div>
          </div>
          
          <div class="app-showcase">
            <h3>Simple and Intuitive Interface</h3>
            <div class="showcase-container">
              <div class="phone-mockup">
                <div class="phone-frame">
                  <div class="phone-screen">
                    <div class="app-ui">
                      <!-- App Header -->
                      <div class="app-header">
                        <div class="app-status-bar"></div>
                        <div class="app-title">
                          <img class="app-icon" src="./assets/echomp3-logo.svg" alt="VA">
                          <span>VA Video to MP3 Converter</span>
                        </div>
                      </div>
                      <!-- App Content -->
                      <div class="app-content">
                        <!-- 转换区域 -->
                        <div class="convert-section">
                          <div class="section-title">Select Video</div>
                          <div class="file-selector">
                            <div class="file-button">
                              <input id="file-input" type="file" accept="video/*" hidden @change="handleFileChange">
                              <label for="file-input">Select Video</label>
                            </div>
                          </div>
                          <div class="file-list" v-if="selectedFile">
                            <div class="file-item">
                              <div class="file-icon"></div>
                              <div class="file-info">
                                <div class="file-name">{{ selectedFile.name }}</div>
                                <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
                              </div>
                              <div class="convert-btn" @click="convertFile">Convert</div>
                            </div>
                          </div>
                        </div>

                        <!-- 设置区域 -->
                        <div class="settings-section">
                          <div class="section-title">Settings</div>
                          <div class="setting-item">
                            <div class="setting-label">Quality</div>
                            <div class="setting-control">
                              <div class="select-wrapper">
                                <select class="setting-select" v-model="quality">
                                  <option value="128">128 kbps</option>
                                  <option value="192">192 kbps</option>
                                  <option value="256">256 kbps</option>
                                  <option value="320">320 kbps</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div class="setting-item">
                            <div class="setting-label">Format</div>
                            <div class="setting-control">
                              <div class="select-wrapper">
                                <select class="setting-select" v-model="format">
                                  <option value="mp3">MP3</option>
                                  <option value="aac">AAC</option>
                                  <option value="wav">WAV</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div class="setting-item">
                            <div class="setting-label">Trim</div>
                            <div class="setting-control">
                              <div class="select-wrapper">
                                <select class="setting-select" v-model="trim">
                                  <option value="no">No trim</option>
                                  <option value="start">Trim start</option>
                                  <option value="both">Trim both</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- 输出区域 -->
                        <div class="output-section">
                          <div class="recent-title">Recent Files</div>
                          <div class="recent-list" v-if="recentFiles.length > 0">
                            <div class="recent-item" v-for="(file, index) in recentFiles" :key="index">
                              <div class="mp3-icon"></div>
                              <div class="file-info">
                                <div class="result-name">{{ file.name }}</div>
                                <div class="result-info">{{ file.size }} - {{ file.date }}</div>
                              </div>
                              <div class="action-btns">
                                <div class="play-btn"></div>
                                <div class="download-btn"></div>
                              </div>
                            </div>
                          </div>
                          <div class="empty-list" v-else>
                            <p>No converted files yet.</p>
                          </div>
                        </div>
                      </div>

                      <!-- App Navigation -->
                      <div class="app-nav">
                        <div class="nav-item active">
                          <div class="nav-icon active"></div>
                          <div class="nav-label">Convert</div>
                        </div>
                        <div class="nav-item">
                          <div class="nav-icon"></div>
                          <div class="nav-label">History</div>
                        </div>
                        <div class="nav-item">
                          <div class="nav-icon"></div>
                          <div class="nav-label">Settings</div>
                        </div>
                        <div class="nav-item">
                          <div class="nav-icon"></div>
                          <div class="nav-label">About</div>
                        </div>
                      </div>

                      <!-- 进度条 -->
                      <div class="progress-container" v-if="isConverting">
                        <div class="progress-overlay"></div>
                        <div class="progress-modal">
                          <div class="progress-title">Converting...</div>
                          <div class="progress-bar-container">
                            <div class="progress-bar" :style="{width: `${progress}%`}"></div>
                          </div>
                          <div class="progress-text">{{ progress }}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="showcase-features">
                <div class="showcase-feature">
                  <div class="showcase-icon">⚡</div>
                  <div class="showcase-text">
                    <h4>Fast Conversion</h4>
                    <p>Convert videos to MP3 with optimized speed and efficiency</p>
                  </div>
                </div>
                <div class="showcase-feature">
                  <div class="showcase-icon">📱</div>
                  <div class="showcase-text">
                    <h4>User-Friendly Design</h4>
                    <p>Simple three-step process: select, customize, convert</p>
                  </div>
                </div>
                <div class="showcase-feature">
                  <div class="showcase-icon">🔄</div>
                  <div class="showcase-text">
                    <h4>Batch Processing</h4>
                    <p>Convert multiple files at once to save time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="download-reminder">
            <div class="reminder-content">
              <h3>Ready to convert videos to MP3?</h3>
              <p>Download VA Video to MP3 converter now and start creating high-quality audio files</p>
              <a href="#download" class="reminder-btn">Download Now</a>
            </div>
          </div>
        </main>
        
        <footer class="footer">
          <div class="footer-content">
            <p>&copy; 2023 VA Video to MP3 converter. All rights reserved</p>
            <div class="footer-links">
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
              <a href="#">About Us</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
    
    <!-- 安卓提示框 -->
    <div class="android-notice-overlay" v-if="showAndroidNoticeModal" @click="closeAndroidNotice">
      <div class="android-notice-modal" @click.stop>
        <div class="notice-icon-container">
          <div class="notice-icon-circle">
            <span class="notice-emoji">📱</span>
          </div>
        </div>
        <h3>Coming Soon!</h3>
        <p>The app is not yet available on Android.</p>
        <p class="notice-subtext">We're working hard to bring it to Android devices soon.</p>
        <button class="notice-close-btn" @click="closeAndroidNotice">Got it</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VA Video to MP3 converter',
  data() {
    return {
      isLoading: true,
      isAndroid: false,
      isIOS: false,
      userAgent: '',
      showAndroidNoticeModal: false,
      selectedFile: null,
      quality: '320',
      format: 'mp3',
      trim: 'no',
      recentFiles: [],
      isConverting: false,
      progress: 0
    }
  },
  mounted() {
    // 模拟加载过程，2秒后淡出加载界面
    setTimeout(() => {
      this.isLoading = false
    }, 2000)
    
    // 检测设备类型
    this.detectDevice()
    
    // 监听userAgent变化（用于浏览器设备模拟测试）
    window.addEventListener('resize', this.detectDevice)
  },
  beforeUnmount() {
    // 移除事件监听
    window.removeEventListener('resize', this.detectDevice)
  },
  methods: {
    detectDevice() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      this.userAgent = userAgent
      
      // 检测iOS设备
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        this.isIOS = true
        this.isAndroid = false
        console.log('设备检测: iOS设备')
        console.log('userAgent:', userAgent)
      } 
      // 检测Android设备
      else if (/android/i.test(userAgent)) {
        this.isAndroid = true
        this.isIOS = false
        console.log('设备检测: Android设备')
        console.log('userAgent:', userAgent)
      }
      // 默认情况（PC或其他设备）
      else {
        this.isAndroid = false
        this.isIOS = false
        console.log('设备检测: 其他设备（PC等）')
        console.log('userAgent:', userAgent)
      }
      
      // 打印当前状态
      console.log('当前状态 - isAndroid:', this.isAndroid, 'isIOS:', this.isIOS)
      
      // 监听媒体查询变化（响应式设计测试）
      this.checkMediaQuery()
    },
    
    checkMediaQuery() {
      // 检查设备方向和宽度
      const isPortrait = window.matchMedia("(orientation: portrait)").matches
      const isMobile = window.matchMedia("(max-width: 768px)").matches
      
      console.log('屏幕方向:', isPortrait ? '竖屏' : '横屏')
      console.log('是否移动设备宽度:', isMobile)
      
      // 可以在这里添加其他响应式逻辑
    },
    
    showAndroidNotice() {
      this.showAndroidNoticeModal = true
    },
    closeAndroidNotice() {
      this.showAndroidNoticeModal = false
    },
    handleFileChange(event) {
      this.selectedFile = event.target.files[0]
    },
    convertFile() {
      this.isConverting = true
      // 这里应该添加实际的文件转换逻辑
      setTimeout(() => {
        this.recentFiles.push({
          name: this.selectedFile.name,
          size: this.formatFileSize(this.selectedFile.size),
          date: new Date().toLocaleString()
        })
        this.isConverting = false
        this.progress = 100
      }, 2000)
    },
    formatFileSize(size) {
      const units = ['B', 'KB', 'MB', 'GB']
      let unitIndex = 0
      let formattedSize = size
      while (formattedSize >= 1024 && unitIndex < units.length - 1) {
        formattedSize /= 1024
        unitIndex++
      }
      return `${formattedSize.toFixed(2)} ${units[unitIndex]}`
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Raleway:wght@300;400;500;600;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --primary-color: #FF5252;
  --primary-dark: #D32F2F;
  --secondary-color: #2979FF;
  --accent-color: #00C853;
  --text-dark: #2D3436;
  --text-light: #636E72;
  --white: #FFFFFF;
  --off-white: #F7F7F7;
  --bg-gradient: linear-gradient(120deg, #FF5252, #2979FF);
  --shadow-sm: 0 4px 6px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 12px 32px rgba(0, 0, 0, 0.1);
  --border-radius-sm: 8px;
  --border-radius-md: 12px;
  --border-radius-lg: 20px;
  --spacing-xs: 8px;
  --spacing-sm: 16px;
  --spacing-md: 24px;
  --spacing-lg: 32px;
  --spacing-xl: 48px;
  --transition: all 0.3s ease;
  --custom-glass: rgba(255, 255, 255, 0.7);
  --glass-border: 1px solid rgba(255, 255, 255, 0.2);
  --glass-shadow: 0 8px 32px rgba(31, 38, 135, 0.1);
  --font-primary: 'Raleway', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  --font-secondary: 'Poppins', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  --font-weight-light: 300;
  --font-weight-regular: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;
}

body {
  font-family: var(--font-primary);
  background-color: var(--off-white);
  color: var(--text-dark);
  line-height: 1.6;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  position: relative;
  min-height: 100vh;
  letter-spacing: 0.01em;
}

.gradient-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(-45deg, rgba(255, 82, 82, 0.05), rgba(41, 121, 255, 0.05), rgba(0, 200, 83, 0.05));
  background-size: 400% 400%;
  animation: gradientBG 15s ease infinite;
  opacity: 0.1;
  z-index: -1;
}

.wave-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23FF5252' fill-opacity='0.05' d='M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,224C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E");
  background-size: cover;
  background-position: bottom;
  z-index: -2;
  opacity: 0.5;
}

.particles {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
}

.particle {
  position: absolute;
  border-radius: 50%;
  background: var(--primary-color);
  opacity: 0.3;
  animation: float 15s infinite ease-in-out;
}

.particle-1 {
  width: 80px;
  height: 80px;
  left: 10%;
  top: 10%;
  animation-delay: 0s;
  animation-duration: 20s;
  background: var(--primary-color);
  filter: blur(20px);
}

.particle-2 {
  width: 120px;
  height: 120px;
  right: 10%;
  top: 40%;
  animation-delay: -5s;
  animation-duration: 25s;
  background: var(--secondary-color);
  filter: blur(30px);
}

.particle-3 {
  width: 60px;
  height: 60px;
  left: 20%;
  bottom: 30%;
  animation-delay: -2s;
  animation-duration: 18s;
  background: var(--accent-color);
  filter: blur(15px);
}

.particle-4 {
  width: 100px;
  height: 100px;
  right: 25%;
  bottom: 15%;
  animation-delay: -7s;
  animation-duration: 22s;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  filter: blur(25px);
}

.particle-5 {
  width: 70px;
  height: 70px;
  left: 40%;
  top: 60%;
  animation-delay: -10s;
  animation-duration: 16s;
  background: var(--secondary-color);
  filter: blur(18px);
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) translateX(0);
  }
  25% {
    transform: translateY(-30px) translateX(15px);
  }
  50% {
    transform: translateY(-15px) translateX(30px);
  }
  75% {
    transform: translateY(-25px) translateX(-15px);
  }
}

.content-wrapper {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  z-index: 1;
}

.header {
  padding: var(--spacing-md) 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: var(--spacing-lg);
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.8));
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.8);
  transform: translateY(0);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), 
              box-shadow 0.4s ease,
              background-color 0.4s ease;
}

.logo-container:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.logo {
  width: 40px;
  height: 40px;
  margin-right: var(--spacing-sm);
  object-fit: contain;
}

.header h1 {
  font-size: 2.2rem;
  color: var(--primary-color);
  font-weight: 700;
  letter-spacing: -1px;
  font-family: var(--font-secondary);
}

.main-content {
  padding: var(--spacing-md) 0;
  display: flex;
  flex-direction: column;
}

.hero {
  text-align: center;
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-xl) 0;
  position: relative;
  overflow: hidden;
  border-radius: var(--border-radius-lg);
  background-color: var(--white);
  box-shadow: var(--shadow-lg);
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.8);
}

.hero-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--bg-gradient);
  opacity: 0.05;
  z-index: -1;
}

.hero h2 {
  font-size: 3rem;
  margin-bottom: var(--spacing-md);
  color: var(--primary-dark);
  font-weight: 700;
  letter-spacing: -1px;
  font-family: var(--font-secondary);
  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.subtitle {
  font-size: 1.3rem;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto;
  font-weight: var(--font-weight-light);
  line-height: 1.5;
}

.hero-cta {
  margin-top: var(--spacing-md);
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 36px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: var(--white);
  text-decoration: none;
  border-radius: 50px;
  font-weight: var(--font-weight-semibold);
  font-size: 1.2rem;
  letter-spacing: 0.5px;
  box-shadow: 0 10px 25px rgba(255, 82, 82, 0.3);
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275),
              box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.cta-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(255, 82, 82, 0.4);
}

.cta-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--primary-dark), var(--primary-color));
  opacity: 0;
  z-index: -1;
  transition: opacity 0.3s ease;
}

.cta-btn:hover::after {
  opacity: 1;
}

.cta-btn .btn-icon {
  margin-left: var(--spacing-xs);
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.cta-btn:hover .btn-icon {
  transform: translateY(3px);
}

.app-title-container {
  display: flex;
  flex-direction: column;
}

.app-subtitle {
  font-size: 1rem;
  color: var(--text-light);
  font-weight: var(--font-weight-medium);
  margin-top: -5px;
}

.app-showcase {
  padding: var(--spacing-xl) 0;
  margin-bottom: var(--spacing-xl);
  position: relative;
}

.app-showcase h3 {
  text-align: center;
  font-size: 2.2rem;
  margin-bottom: var(--spacing-xl);
  color: var(--primary-dark);
  font-weight: 600;
  animation: fadeIn 0.8s forwards;
  animation-delay: 0.3s;
  font-family: var(--font-secondary);
  position: relative;
  display: inline-block;
  padding-bottom: 10px;
}

.showcase-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  padding: var(--spacing-xl);
  animation: fadeIn 0.8s forwards;
  animation-delay: 0.4s;
  overflow: hidden;
  position: relative;
}

.showcase-container::before {
  content: '';
  position: absolute;
  top: -10px;
  right: -10px;
  bottom: -10px;
  left: -10px;
  background: linear-gradient(45deg, rgba(255, 82, 82, 0.05), rgba(41, 121, 255, 0.05));
  border-radius: calc(var(--border-radius-lg) + 10px);
  z-index: -1;
  filter: blur(10px);
}

.phone-mockup {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 280px;
}

.phone-frame {
  width: 280px;
  height: 560px;
  background: linear-gradient(145deg, #1a1a1a, #2a2a2a);
  border-radius: 40px;
  padding: 12px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 8px rgba(255, 255, 255, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  position: relative;
  transform: perspective(1000px) rotateY(-5deg) rotateX(2deg);
  transition: transform 0.5s ease, box-shadow 0.5s ease;
}

.phone-frame::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  height: 28px;
  background: #000;
  border-radius: 0 0 20px 20px;
  z-index: 2;
}

.phone-frame::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -2px;
  transform: translateY(-50%);
  width: 4px;
  height: 80px;
  background: #333;
  border-radius: 2px;
  z-index: 2;
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 32px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.1);
}

.app-ui {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-header {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  padding: 8px 10px;
  color: white;
  border-radius: 32px 32px 0 0;
  position: relative;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
  flex-shrink: 0;
}

.app-status-bar {
  height: 18px;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
}

.app-status-bar::before {
  content: '';
  display: flex;
  width: 60px;
  height: 4px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 2px;
}

.app-status-bar::after {
  content: '14:25';
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
}

.app-title {
  font-weight: 600;
  font-size: 15px;
  text-align: center;
  padding: 4px 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
}

.app-title::before {
  content: '';
  display: block;
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M12 3v9.28a4.39 4.39 0 0 0-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z'/%3E%3C/svg%3E");
  background-size: contain;
  margin-right: 5px;
  opacity: 0.9;
}

.app-content {
  flex: 1;
  padding: 10px;
  padding-bottom: 65px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f8f9fa;
  position: relative;
  z-index: 5;
}

.app-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(to bottom, rgba(255, 82, 82, 0.05), transparent);
  z-index: 1;
  pointer-events: none;
}

.section-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: var(--text-dark);
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 3px;
  height: 12px;
  background: var(--primary-color);
  margin-right: 6px;
  border-radius: 2px;
}

.file-selector {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.file-button {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: white;
  border-radius: 18px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 3px 8px rgba(255, 82, 82, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent);
  border-radius: 18px;
}

.file-button::after {
  content: '+';
  font-size: 14px;
  font-weight: bold;
  margin-left: 4px;
  opacity: 0.9;
}

.file-item {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 6px;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  overflow: hidden;
  z-index: 4;
}

.file-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #e9ecef, #dee2e6);
  border-radius: 6px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

.file-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-dark);
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 10px;
  color: var(--text-light);
  display: flex;
  align-items: center;
}

.convert-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: white;
  padding: 6px 10px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 500;
  box-shadow: 0 3px 8px rgba(255, 82, 82, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  margin-left: 6px;
  flex-shrink: 0;
}

.convert-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent);
  border-radius: 14px;
}

.convert-btn:active {
  transform: scale(0.95);
  box-shadow: 0 2px 8px rgba(255, 82, 82, 0.2);
}

.divider {
  height: 1px;
  background: #e9ecef;
  margin: 12px 0;
  position: relative;
}

.divider::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 3px;
  background: #f0f0f0;
  border-radius: 2px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 8px;
  color: var(--text-dark);
  padding: 5px 0;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.05);
}

.setting-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.setting-item span:first-child {
  color: var(--text-light);
}

.setting-item span:last-child {
  font-weight: 500;
  color: var(--text-dark);
  background: rgba(255, 82, 82, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.recent-files {
  margin-top: 10px;
}

.recent-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: var(--text-dark);
  display: flex;
  align-items: center;
}

.recent-title::before {
  content: '';
  width: 3px;
  height: 12px;
  background: var(--primary-color);
  margin-right: 6px;
  border-radius: 2px;
}

.recent-list {
  margin-bottom: 8px;
}

.recent-item {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 6px;
  position: relative;
  overflow: hidden;
  z-index: 4;
}

.mp3-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #e9ecef, #dee2e6);
  border-radius: 6px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

.result-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-dark);
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-info {
  font-size: 10px;
  color: var(--text-light);
  display: flex;
  align-items: center;
}

.action-btns {
  display: flex;
  margin-left: 6px;
  flex-shrink: 0;
}

.play-btn,
.download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  margin-left: 6px;
}

.play-btn {
  background: linear-gradient(135deg, #4dabf7, #3b8ce4);
  box-shadow: 0 2px 5px rgba(59, 140, 228, 0.2);
}

.download-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  box-shadow: 0 2px 5px rgba(255, 82, 82, 0.2);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
  padding-top: 4px;
}

.nav-item:nth-child(3) {
  opacity: 1;
}

.nav-item:nth-child(3) .nav-icon {
  color: var(--primary-color);
  background: rgba(255, 82, 82, 0.1);
}

.nav-item:active {
  transform: scale(0.95);
}

.nav-icon {
  width: 20px;
  height: 20px;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--text-light);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  transition: background 0.2s, color 0.2s;
}

.nav-text {
  font-size: 9px;
  color: var(--text-light);
}

.nav-item:nth-child(3) .nav-text {
  color: var(--primary-color);
  font-weight: 500;
}

.output-section {
  margin-bottom: 25px;
}

.progress-container {
  margin-top: 12px;
  position: relative;
  z-index: 5;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  margin-bottom: 6px;
  color: var(--text-light);
}

.progress-label span:last-child {
  font-weight: 500;
}

.progress-bar {
  height: 7px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  width: 65%;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-dark));
  border-radius: 4px;
  animation: progress 2s ease-in-out infinite;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 20px;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3));
}

@keyframes progress {
  0% { width: 0%; }
  50% { width: 65%; }
  100% { width: 100%; }
}

@media (max-width: 992px) {
  .showcase-container {
    flex-direction: column;
    padding: var(--spacing-md);
  }
  
  .phone-mockup {
    margin-bottom: var(--spacing-lg);
  }
}

.download-section {
  order: -1;
  margin-top: 0;
  padding: var(--spacing-xl) var(--spacing-lg);
  padding-top: calc(var(--spacing-xl) + 25px);
  position: relative;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95));
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: var(--shadow-md), 0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07);
  z-index: 2;
  overflow: hidden;
}

.download-section::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff5252' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
  z-index: -1;
}

.download-section h3 {
  text-align: center;
  font-size: 2.2rem;
  margin-bottom: var(--spacing-sm);
  color: var(--primary-dark);
  font-weight: 700;
  font-family: var(--font-secondary);
  position: relative;
  padding-bottom: 15px;
}

.download-section h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  border-radius: 3px;
}

.download-desc {
  text-align: center;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto var(--spacing-lg);
  font-size: 1.1rem;
}

.download-badge {
  position: absolute;
  top: 25px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  color: white;
  padding: 8px 20px;
  border-radius: 30px;
  font-weight: var(--font-weight-semibold);
  font-size: 0.9rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  z-index: 10;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 82, 82, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
  }
}

.download-options {
  margin-top: var(--spacing-xl);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-xl);
  position: relative;
}

.download-option {
  flex: 1;
  min-width: 280px;
  max-width: 400px;
  border-radius: var(--border-radius-md);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: var(--spacing-xl) var(--spacing-md);
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.03);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.download-option::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
}

.android::before {
  background: linear-gradient(90deg, var(--primary-color), var(--primary-dark));
}

.ios::before {
  background: linear-gradient(90deg, var(--secondary-color), #2962FF);
}

.download-option:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 15px 15px rgba(0, 0, 0, 0.05);
}

.platform-label {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.05);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  color: var(--text-light);
}

.icon-container {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6));
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-md);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05), inset 0 1px 1px rgba(255, 255, 255, 0.8);
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.7);
}

.download-option:hover .icon-container {
  transform: scale(1.05) translateY(-3px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08), inset 0 1px 1px rgba(255, 255, 255, 0.8);
}

.platform-icon {
  width: 45px;
  height: 45px;
}

.download-option h4 {
  font-size: 1.5rem;
  margin-bottom: var(--spacing-sm);
  color: var(--text-dark);
  font-weight: 600;
  font-family: var(--font-secondary);
}

.version-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.version {
  display: inline-block;
  background: rgba(255, 82, 82, 0.1);
  color: var(--primary-color);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: var(--font-weight-medium);
}

.ios .version {
  background: rgba(41, 121, 255, 0.1);
  color: var(--secondary-color);
}

.rating {
  display: flex;
  align-items: center;
}

.star, .star-half {
  color: #FFB400;
  font-size: 0.9rem;
  letter-spacing: -2px;
}

.star-half {
  opacity: 0.5;
}

.rating-number {
  margin-left: 5px;
  font-weight: var(--font-weight-medium);
  color: var(--text-dark);
}

.compatible {
  color: var(--text-light);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-md);
}

.android-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  box-shadow: 0 10px 20px rgba(255, 82, 82, 0.2), 0 5px 8px rgba(0, 0, 0, 0.05);
}

.ios-btn {
  background: linear-gradient(135deg, var(--secondary-color), #2962FF);
  box-shadow: 0 10px 20px rgba(41, 121, 255, 0.2), 0 5px 8px rgba(0, 0, 0, 0.05);
}

.download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 30px;
  color: var(--white);
  text-decoration: none;
  border-radius: 50px;
  font-weight: var(--font-weight-semibold);
  font-size: 1.05rem;
  margin-bottom: var(--spacing-md);
  position: relative;
  overflow: hidden;
  z-index: 1;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.download-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: 0.5s;
}

.download-btn:hover {
  transform: translateY(-2px);
}

.download-btn:hover::before {
  left: 100%;
}

.android-btn:hover {
  box-shadow: 0 15px 25px rgba(255, 82, 82, 0.3), 0 5px 10px rgba(0, 0, 0, 0.08);
}

.ios-btn:hover {
  box-shadow: 0 15px 25px rgba(41, 121, 255, 0.3), 0 5px 10px rgba(0, 0, 0, 0.08);
}

.btn-text {
  position: relative;
  z-index: 1;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.5px;
}

.btn-icon {
  margin-left: var(--spacing-xs);
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.download-btn:hover .btn-icon {
  transform: translateX(4px);
}

.download-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: var(--spacing-md);
}

.meta-item {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-light);
  font-size: 0.85rem;
}

.meta-icon {
  margin-right: 6px;
  font-size: 0.9rem;
}

.qr-download {
  margin-top: var(--spacing-md);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-code {
  width: 90px;
  height: 90px;
  background: var(--white);
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  padding: 5px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.download-option:hover .qr-code {
  transform: scale(1.05);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.qr-inner {
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cstyle%3E.qr %7B fill: %23000; %7D%3C/style%3E%3Cg class='qr'%3E%3Crect x='10' y='10' width='10' height='10'/%3E%3Crect x='20' y='10' width='10' height='10'/%3E%3Crect x='30' y='10' width='10' height='10'/%3E%3Crect x='10' y='20' width='10' height='10'/%3E%3Crect x='30' y='20' width='10' height='10'/%3E%3Crect x='10' y='30' width='10' height='10'/%3E%3Crect x='20' y='30' width='10' height='10'/%3E%3Crect x='30' y='30' width='10' height='10'/%3E%3Crect x='60' y='10' width='10' height='10'/%3E%3Crect x='70' y='10' width='10' height='10'/%3E%3Crect x='80' y='10' width='10' height='10'/%3E%3Crect x='60' y='20' width='10' height='10'/%3E%3Crect x='80' y='20' width='10' height='10'/%3E%3Crect x='60' y='30' width='10' height='10'/%3E%3Crect x='70' y='30' width='10' height='10'/%3E%3Crect x='80' y='30' width='10' height='10'/%3E%3Crect x='10' y='60' width='10' height='10'/%3E%3Crect x='20' y='60' width='10' height='10'/%3E%3Crect x='30' y='60' width='10' height='10'/%3E%3Crect x='10' y='70' width='10' height='10'/%3E%3Crect x='30' y='70' width='10' height='10'/%3E%3Crect x='10' y='80' width='10' height='10'/%3E%3Crect x='20' y='80' width='10' height='10'/%3E%3Crect x='30' y='80' width='10' height='10'/%3E%3Crect x='50' y='50' width='10' height='10'/%3E%3Crect x='40' y='50' width='10' height='10'/%3E%3Crect x='50' y='40' width='10' height='10'/%3E%3Crect x='40' y='70' width='10' height='10'/%3E%3Crect x='50' y='70' width='10' height='10'/%3E%3Crect x='40' y='40' width='10' height='10'/%3E%3C/g%3E%3C/svg%3E");
  background-size: cover;
}

.qr-download p {
  font-size: 0.8rem;
  color: var(--text-light);
}

.download-info {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
  margin-bottom: var(--spacing-lg);
  background: rgba(255, 255, 255, 0.6);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
}

.info-item {
  flex: 1;
  min-width: 200px;
  max-width: 300px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.info-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
}

.info-icon {
  font-size: 1.8rem;
  margin-right: var(--spacing-sm);
}

.info-text h5 {
  font-weight: var(--font-weight-semibold);
  margin-bottom: 2px;
  color: var(--text-dark);
}

.info-text p {
  font-size: 0.85rem;
  color: var(--text-light);
  margin: 0;
}

.download-notice {
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.03);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  margin-top: var(--spacing-lg);
}

.notice-icon {
  margin-right: var(--spacing-sm);
  width: 24px;
  height: 24px;
  min-width: 24px;
  background: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

.download-notice p {
  font-size: 0.9rem;
  color: var(--text-light);
  margin: 0;
}

.notice-link {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: color 0.2s ease;
}

.notice-link:hover {
  color: var(--primary-dark);
  text-decoration: underline;
}

@media (max-width: 768px) {
  .download-info {
    flex-direction: column;
  }
  
  .download-option {
    width: 100%;
  }
  
  .footer-content {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-sm);
  }
  
  .footer-links {
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--spacing-sm);
  }
  
  .download-reminder {
    margin-top: var(--spacing-xl);
    padding: var(--spacing-md);
  }
  
  .download-reminder h3 {
    font-size: 1.5rem;
  }
  
  .download-reminder p {
    font-size: 1rem;
  }
  
  .download-reminder .reminder-btn {
    width: 100%;
    margin-top: var(--spacing-sm);
  }
  
  .notice-close-btn {
    width: 100%;
  }
  
  .android-notice-modal {
    max-width: 320px;
    padding: var(--spacing-lg);
  }
  
  .android-notice-modal h3 {
    font-size: 1.5rem;
  }
  
  .android-notice-modal p {
    font-size: 1rem;
  }
  
  .download-options {
    gap: var(--spacing-lg);
  }

  .feature-list {
    flex-direction: column;
    align-items: center;
  }
  
  .feature-item {
    width: 100%;
  }
  
  .features h3 {
    font-size: 1.8rem;
    margin-bottom: var(--spacing-lg);
  }
  
  /* 底部优化 */
  .showcase-container {
    padding: var(--spacing-md) var(--spacing-sm);
  }
  
  .download-section {
    padding: calc(var(--spacing-lg) + 30px) var(--spacing-sm) var(--spacing-lg);
  }
  
  .download-notice {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .notice-icon {
    margin-bottom: var(--spacing-xs);
  }
  
  .download-notice p {
    font-size: 0.85rem;
  }
  
  .qr-download {
    display: none;
  }
  
  .phone-frame {
    transform: none;
    width: 240px;
    height: 480px;
  }
  
  .download-meta {
    margin-bottom: 0;
  }
  
  .app-showcase h3 {
    font-size: 1.8rem;
    margin-bottom: var(--spacing-lg);
  }
}

@media (max-width: 576px) {
  .hero h2 {
    font-size: 2rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .download-section h3 {
    font-size: 1.8rem;
  }
  
  .download-option {
    padding: var(--spacing-lg) var(--spacing-sm);
  }
  
  .download-badge {
    font-size: 0.8rem;
    padding: 6px 16px;
  }
  
  .header h1 {
    font-size: 1.8rem;
  }
  
  .app-subtitle {
    font-size: 0.9rem;
  }
  
  .logo {
    width: 32px;
    height: 32px;
  }
  
  /* 小屏幕设备底部优化 */
  .download-meta .meta-item {
    font-size: 0.75rem;
  }
  
  .compatible {
    font-size: 0.8rem;
  }
  
  .version-wrapper {
    flex-direction: column;
    gap: var(--spacing-xs);
  }
  
  .download-btn {
    padding: 12px 20px;
    font-size: 0.95rem;
  }
  
  .feature-icon {
    font-size: 2.5rem;
  }
  
  .feature-item h4 {
    font-size: 1.2rem;
  }
  
  .feature-item p {
    font-size: 0.9rem;
  }
  
  .download-info {
    padding: var(--spacing-sm);
  }
  
  .info-item {
    padding: var(--spacing-xs);
  }
  
  .info-icon {
    font-size: 1.5rem;
  }
  
  .info-text h5 {
    font-size: 0.9rem;
  }
  
  .info-text p {
    font-size: 0.75rem;
  }
}

/* 最小屏幕设备优化 */
@media (max-width: 360px) {
  .download-option {
    padding: var(--spacing-md) var(--spacing-xs);
  }
  
  .phone-frame {
    width: 200px;
    height: 400px;
  }
  
  .hero h2 {
    font-size: 1.8rem;
  }
  
  .logo-container {
    padding: var(--spacing-xs);
  }
  
  .header h1 {
    font-size: 1.5rem;
  }
  
  .app-subtitle {
    font-size: 0.8rem;
  }
  
  .logo {
    width: 28px;
    height: 28px;
  }
}

.phone-frame:hover {
  transform: perspective(1000px) rotateY(0deg) rotateX(0deg) translateY(-10px);
  box-shadow: 
    0 30px 60px rgba(0, 0, 0, 0.25),
    0 0 0 6px rgba(255, 255, 255, 0.2),
    -5px 10px 25px rgba(0, 0, 0, 0.1);
}

.footer {
  padding: var(--spacing-lg) 0;
  margin-top: var(--spacing-xl);
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.8);
}

.footer p {
  color: var(--text-light);
}

.footer-links {
  display: flex;
  gap: var(--spacing-md);
}

.footer-links a {
  color: var(--primary-color);
  text-decoration: none;
  transition: var(--transition);
}

.footer-links a:hover {
  color: var(--primary-dark);
  text-decoration: underline;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.header, .hero, .download-section, .features, .footer {
  opacity: 0;
  animation: contentFadeIn 0.8s forwards;
}

.hero {
  animation-delay: 0.3s;
}

.download-section {
  animation-delay: 0.5s;
}

.features {
  animation-delay: 0.7s;
}

.footer {
  animation-delay: 0.9s;
}

@keyframes contentFadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes gradientBG {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.decorative-circle {
  position: fixed;
  border-radius: 50%;
  border: 1px solid rgba(255, 82, 82, 0.2);
  pointer-events: none;
}

.circle-1 {
  width: 400px;
  height: 400px;
  top: -100px;
  right: -100px;
}

.circle-2 {
  width: 300px;
  height: 300px;
  bottom: 100px;
  left: -100px;
}

.decorative-line {
  position: fixed;
  background: linear-gradient(90deg, transparent, rgba(255, 82, 82, 0.2), transparent);
  height: 1px;
  width: 100%;
  pointer-events: none;
}

.line-1 {
  top: 30%;
  transform: rotate(-5deg);
}

.line-2 {
  bottom: 25%;
  transform: rotate(5deg);
}

/* 加载动画相关样式 */
.content-container {
  opacity: 0;
  transition: opacity 1s ease;
}

.content-container.fade-in {
  opacity: 1;
}

.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f8f8f8, #ffffff);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  transition: opacity 0.8s ease, visibility 0.8s ease;
}

.loader-container.fade-out {
  opacity: 0;
  visibility: hidden;
}

.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loader-svg {
  width: 80px;
  height: 80px;
  animation: rotate 2s linear infinite;
}

.loader-circle {
  stroke: url(#gradient);
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

.loader-text {
  margin-top: 20px;
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  font-weight: var(--font-weight-medium);
  color: var(--primary-color);
  animation: pulse-text 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
    stroke: var(--primary-color);
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
    stroke: var(--secondary-color);
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
    stroke: var(--primary-color);
  }
}

@keyframes pulse-text {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

/* 下载选项排序 */
.order-first {
  order: -1;
}

/* 安卓提示框相关样式 */
.android-notice-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  animation: fadeIn 0.3s ease;
}

.android-notice-modal {
  background: white;
  padding: calc(var(--spacing-lg) * 1.5);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  text-align: center;
  max-width: 360px;
  width: 90%;
  transform: translateY(0);
  animation: modalSlideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
}

.android-notice-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
}

.notice-icon-container {
  margin-bottom: var(--spacing-lg);
}

.notice-icon-circle {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  box-shadow: 0 10px 25px rgba(255, 82, 82, 0.3);
  position: relative;
  transform: translateY(0);
  animation: iconBounce 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s both;
}

.notice-icon-circle::after {
  content: '';
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border-radius: 50%;
  border: 2px solid rgba(255, 82, 82, 0.2);
  animation: pulseRing 2s infinite;
}

.notice-emoji {
  font-size: 2.5rem;
  color: white;
}

.android-notice-modal h3 {
  font-size: 1.8rem;
  color: var(--text-dark);
  margin-bottom: var(--spacing-sm);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-secondary);
}

.android-notice-modal p {
  font-size: 1.1rem;
  margin-bottom: var(--spacing-sm);
  color: var(--text-dark);
}

.notice-subtext {
  color: var(--text-light) !important;
  margin-bottom: var(--spacing-lg) !important;
  font-size: 0.95rem !important;
}

.notice-close-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(255, 82, 82, 0.3);
  position: relative;
  overflow: hidden;
}

.notice-close-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 25px rgba(255, 82, 82, 0.4);
}

.notice-close-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: 0.5s;
}

.notice-close-btn:hover::before {
  left: 100%;
}

@keyframes modalSlideIn {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes iconBounce {
  0% { transform: scale(0.8); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes pulseRing {
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 0.4; }
  100% { transform: scale(1); opacity: 0.8; }
}

.download-reminder {
  padding: var(--spacing-xl) var(--spacing-lg);
  margin: var(--spacing-xl) 0;
  text-align: center;
  position: relative;
  border-radius: var(--border-radius-lg);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.85));
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.download-reminder::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 82, 82, 0.1), rgba(41, 121, 255, 0.1));
  z-index: -1;
}

.reminder-content {
  position: relative;
  z-index: 1;
}

.download-reminder h3 {
  font-size: 2rem;
  margin-bottom: var(--spacing-sm);
  color: var(--primary-dark);
  font-weight: 700;
  font-family: var(--font-secondary);
}

.download-reminder p {
  color: var(--text-light);
  margin-bottom: var(--spacing-md);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  font-size: 1.1rem;
}

.reminder-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: var(--white);
  text-decoration: none;
  border-radius: 50px;
  font-weight: var(--font-weight-semibold);
  font-size: 1.1rem;
  box-shadow: 0 10px 25px rgba(255, 82, 82, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
}

.reminder-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: 0.5s;
}

.reminder-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 30px rgba(255, 82, 82, 0.4);
}

.reminder-btn:hover::before {
  left: 100%;
}

.features {
  padding: var(--spacing-xl) 0;
  margin-bottom: var(--spacing-xl);
}

.features h3 {
  text-align: center;
  font-size: 2.2rem;
  margin-bottom: var(--spacing-xl);
  color: var(--primary-dark);
  font-weight: 600;
  font-family: var(--font-secondary);
}

.feature-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
  justify-content: center;
}

.feature-item {
  flex: 1;
  min-width: 280px;
  max-width: 350px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-lg);
  text-align: center;
  box-shadow: var(--shadow-md);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.feature-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.feature-item:hover::before {
  opacity: 1;
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: var(--spacing-md);
}

.feature-item h4 {
  font-size: 1.4rem;
  margin-bottom: var(--spacing-sm);
  color: var(--text-dark);
  font-weight: 600;
  font-family: var(--font-secondary);
}

.feature-item p {
  color: var(--text-light);
  line-height: 1.6;
}

/* 在这里添加移动端媒体查询 */
@media (max-width: 768px) {
  .download-info {
    flex-direction: column;
  }
  
  .download-option {
    width: 100%;
  }
  
  .footer-content {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-sm);
  }
  
  .footer-links {
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--spacing-sm);
  }
  
  .download-reminder {
    margin-top: var(--spacing-xl);
    padding: var(--spacing-md);
  }
  
  .download-reminder h3 {
    font-size: 1.5rem;
  }
  
  .download-reminder p {
    font-size: 1rem;
  }
  
  .download-reminder .reminder-btn {
    width: 100%;
    margin-top: var(--spacing-sm);
  }
  
  .notice-close-btn {
    width: 100%;
  }
  
  .android-notice-modal {
    max-width: 320px;
    padding: var(--spacing-lg);
  }
  
  .android-notice-modal h3 {
    font-size: 1.5rem;
  }
  
  .android-notice-modal p {
    font-size: 1rem;
  }
  
  .download-options {
    gap: var(--spacing-lg);
  }

  .feature-list {
    flex-direction: column;
    align-items: center;
  }
  
  .feature-item {
    width: 100%;
  }
  
  .features h3 {
    font-size: 1.8rem;
    margin-bottom: var(--spacing-lg);
  }
}

/* 触摸设备优化 */
@media (hover: none) {
  .cta-btn:active, .download-btn:active, .reminder-btn:active, .notice-close-btn:active {
    transform: scale(0.97);
  }
  
  .feature-item:active {
    transform: translateY(-2px);
  }
  
  .download-option:active {
    transform: translateY(-2px);
  }
  
  .download-info .info-item:active {
    transform: translateY(-2px);
  }
  
  .phone-frame:active {
    transform: none;
  }
  
  /* 防止触摸设备上悬停效果导致的卡顿 */
  .cta-btn::after, .download-btn::before, .reminder-btn::before, .notice-close-btn::before {
    display: none;
  }
}

.file-item {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  overflow: hidden;
  z-index: 4;
}

.file-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(to bottom, var(--primary-color), var(--primary-dark));
  opacity: 0.7;
}

.file-item:hover {
  background: #f5f5f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.progress-container {
  margin-top: 12px;
  position: relative;
  z-index: 5;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  margin-bottom: 6px;
  color: var(--text-light);
}

.progress-label span:last-child {
  font-weight: 500;
}

.progress-bar {
  height: 7px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  width: 65%;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-dark));
  border-radius: 4px;
  animation: progress 2s ease-in-out infinite;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 20px;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3));
}

@keyframes progress {
  0% { width: 0%; }
  50% { width: 65%; }
  100% { width: 100%; }
}

.convert-section, .output-section {
  background: white;
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 3;
}

.app-nav {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 55px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 4px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03);
  z-index: 100;
  border-radius: 0 0 32px 32px;
}

.settings-section {
  margin-bottom: 15px;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 10px;
  margin-bottom: 6px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.setting-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-dark);
  display: flex;
  align-items: center;
}

.setting-label::before {
  content: '';
  width: 6px;
  height: 6px;
  background: var(--primary-color);
  margin-right: 5px;
  border-radius: 50%;
  opacity: 0.7;
}

.setting-control {
  display: flex;
  align-items: center;
}

.select-wrapper {
  position: relative;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 3px 20px 3px 6px;
  min-width: 80px;
  overflow: hidden;
}

.setting-select {
  font-size: 11px;
  color: var(--text-dark);
  border: none;
  background: transparent;
  appearance: none;
  width: 100%;
  outline: none;
}

.select-wrapper::after {
  content: '▼';
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 8px;
  color: var(--text-light);
  pointer-events: none;
}

.app-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  z-index: 10;
  padding: 0 5px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.04);
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 5px 0;
}

.nav-icon {
  width: 22px;
  height: 22px;
  margin-bottom: 3px;
  opacity: 0.8;
  position: relative;
}

.nav-icon.active {
  color: var(--primary-color);
  opacity: 1;
}

.nav-label {
  font-size: 10px;
  color: var(--text-light);
  transition: color 0.2s;
}

.nav-item.active .nav-label {
  color: var(--primary-color);
  font-weight: 500;
}
</style>
